import { Box, Stack, useTheme } from "@mui/material";
import useLeadInfo from "@/modules/lead-page/hooks/lead-info/lead-info.hook";
import { getLeadPhoneNumberFromGetLeadResponse } from "@/modules/lead-page/utils";
import { GetLeadResponse } from "@/modules/leads/dtos";
import NameSection from "./ui/name";
import OrganizationSection from "./ui/organization";
import StatusLabelSection from "./ui/status-labels";
import LeadLocation from "./ui/location";
import { useLeadLabels } from "@/modules/lead-page/new/utils/lead-labels";
import Label from "@/modules/common/components/ui-kit/label";
import StatusField from "@/modules/lead-page/new/components/header/status-field";
import { memo, useContext } from "react";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import ArchivedOverlay from "@/modules/common/components/ui-kit/archived-overlay";
import Stepper from "@/modules/common/components/ui-kit/stepper/stepper";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import PhoneField from "./ui/phone";
import LeadAssignedUser from "@/modules/lead-page/new/components/sidebar/ui/assigned-user";
import { LeadActions } from "../header/lead-actions";
import CommonIconButton from "@/modules/common/components/ui-kit/buttons/icon-button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import InCallIndicator from "@/modules/common/components/ui-kit/in-call-indicator";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";

interface SpecialIconsProps {
  icons: Array<{ uid: string; imageUrl: string }> | undefined;
}

const SpecialIcons = memo(({ icons }: SpecialIconsProps) => {
  const theme = useTheme();

  if (!icons || icons.length === 0) return null;

  return (
    <Box sx={{ display: "flex", gap: theme.nawy.spacing.xs, flexWrap: "wrap" }}>
      {icons.map((icon) =>
        icon.imageUrl ? (
          <img
            key={icon.uid}
            src={
              icon.imageUrl.startsWith("http://") ||
              icon.imageUrl.startsWith("https://")
                ? icon.imageUrl
                : `https://${icon.imageUrl}`
            }
            style={{ width: "20px", height: "20px" }}
            alt=""
          />
        ) : null,
      )}
    </Box>
  );
});

interface CustomerProfileProps {
  useCompactLayout?: boolean;
  onPersonClick?: () => void;
}

const LeadProfile = ({
  useCompactLayout = false,
  onPersonClick,
}: CustomerProfileProps) => {
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();

  const theme = useTheme();
  const { handleConfirmUpdate, isLoading, isError, lead } = useLeadInfo();
  const { stages } = useContext(LeadPageContext);

  const labels = useLeadLabels(lead);

  const doNotCall = lead?.data?.lead?.do_not_call;
  const isLeadArchived = lead?.data?.lead?.archived;
  const isOnCall = lead?.data?.isOnCall;

  const isLeadActive = () => {
    const validityTime = lead?.data?.lead?.validity_time;
    if (!validityTime) return false;
    return new Date(validityTime) > new Date();
  };

  const phoneNumber = getLeadPhoneNumberFromGetLeadResponse(
    lead as GetLeadResponse,
  );

  const handleUpdateName = (name?: string) => {
    handleConfirmUpdate({
      customer: { name },
    });
  };

  /** 🔹 Compact Layout */
  if (useCompactLayout) {
    return (
      <Box
        sx={{
          width: "100%",
          gap: theme.nawy.spacing.sm,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <CommonIconButton
            variant="outlined-secondary"
            size="md"
            onClick={() => navigate("/")}
          >
            <ArrowLeft
              size={16}
              style={{
                color: theme.nawy.colors.semantic.text.primary,
              }}
            />
          </CommonIconButton>
          <LeadActions />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            gap: theme.nawy.spacing.xs,
          }}
        >
          {/* Left Column */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.xs,
              flex: 1,
              width: "100%",
            }}
          >
            <Stack
              direction="column"
              sx={{
                alignItems: "start",
                width: "100%",
                gap: theme.nawy.spacing.xs,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: theme.nawy.spacing.xs,
                  width: "100%",
                  flexDirection: "column",
                  flexWrap: "wrap",
                }}
              >
                {" "}
                <OrganizationSection
                  name={lead?.data?.lead?.organization?.name}
                />
                <SpecialIcons icons={lead?.data.leadIcons} />
                <Box
                  sx={{
                    display: "flex",
                    gap: theme.nawy.spacing.xs,
                    flexWrap: "wrap",
                  }}
                >
                  <StatusLabelSection
                    type={lead?.data?.lead_type?.lead_type}
                    isActive={isLeadActive()}
                  />
                  {labels.map(({ key, ...props }) => (
                    <Label key={key} {...props} />
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  pr: theme.nawy.spacing.xs,
                  gap: theme.nawy.spacing.xs,
                }}
              >
                <NameSection
                  name={lead?.data?.lead?.name}
                  compact
                  onPersonClick={onPersonClick}
                  isLoading={isLoading}
                  isError={isError}
                  handleConfirmUpdate={handleUpdateName}
                />
              </Box>
              <LeadLocation isArchived={false} />
              <LeadAssignedUser isArchived={isLeadArchived} />
            </Stack>
          </Box>

          {/* Right Column */}
        </Box>
        <PhoneField
          phoneNumber={phoneNumber}
          doNotCall={!!doNotCall}
          isOnCall={!!isOnCall}
          agentName={lead?.data?.lead?.user?.name}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: theme.nawy.spacing.lg,
      }}
    >
      <Stack
        direction="column"
        width="100%"
        display="flex"
        flexDirection="column"
        gap={theme.nawy.spacing.sm}
      >
        <Box display="flex" flexDirection="column" gap={theme.nawy.spacing.xs}>
          <OrganizationSection name={lead?.data?.lead?.organization?.name} />
          <SpecialIcons icons={lead?.data.leadIcons} />
          <Box
            sx={{
              display: "flex",
              gap: theme.nawy.spacing.xs,
              flexWrap: "wrap",
            }}
          >
            <StatusLabelSection
              type={lead?.data?.lead_type?.lead_type}
              isActive={isLeadActive()}
            />
            {labels.map(({ key, ...props }) => (
              <Label key={key} {...props} />
            ))}
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pr: theme.nawy.spacing.xs,
              gap: theme.nawy.spacing.xs,
            }}
          >
            <NameSection
              name={lead?.data?.lead?.name}
              isLoading={isLoading}
              isError={isError}
              handleConfirmUpdate={handleUpdateName}
            />
            <PermissionGuard
              permissions={[
                {
                  name: PermissionsEnum.CAN_VIEW_IN_CALL_TAG,
                  level: PermissionLevel.READ,
                },
              ]}
            >
              {isOnCall && (
                <InCallIndicator
                  agentName={lead?.data?.lead?.user?.name}
                  variant="secondary"
                  testId="lead-in-call-indicator"
                />
              )}
            </PermissionGuard>
          </Box>
          <LeadLocation isArchived={isLeadArchived} />
          <LeadAssignedUser isArchived={isLeadArchived} />
          <PhoneField phoneNumber={phoneNumber} doNotCall={!!doNotCall} />
        </Box>

        {!isMobile && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: theme.nawy.spacing.sm,
              }}
            >
              {stages?.data && stages.data.length > 0 && (
                <ArchivedOverlay isArchived={isLeadArchived}>
                  <Stepper stepsData={stages} />
                </ArchivedOverlay>
              )}
              <StatusField />
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default LeadProfile;
